import { Box, Container, Paper, Tab, Tabs, Typography, ThemeProvider, createTheme } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageIcon from '@mui/icons-material/Message';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import EmailIcon from '@mui/icons-material/Email';
import ForumIcon from '@mui/icons-material/Forum';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { MessagesTab } from './tabs/messages/MessagesTab';
import { MessageLogsTab } from './tabs/message-logs/MessageLogsTab';
import { SessionsTab } from './tabs/sessions/SessionsTab';
import { CampaignsTab } from './tabs/campaigns/CampaignsTab';
import { CampaignLogsTab } from './tabs/campaign-logs/CampaignLogsTab';
import { SubscriptionsTab } from './tabs/subscriptions/SubscriptionsTab';
import { EmailsTab } from './tabs/emails/EmailsTab';
import moment from 'moment';
import { DBMessageLogThunks, DBUserMessageThunks, DBSessionThunks, DBCampaignThunks, DBCampaignLogThunks, DBSubscriptionThunks, DBEmailSendThunks, subscriptionSelectByQuery } from '@common-reducers/DBServiceThunks';
import { SubscriptionObject } from '../../../bt-common/services/subscription-object.class';
import { selectCurrentWorkspace } from '@common-reducers/UserSelectors';
import { Workspace } from '@common/models/workspace';
import { UserOverviewHeader } from './components/user-overview-header';
import { connectNotifierAsSupportedUser } from '@common/noifier-module/NotifierThunks';

const TAB_NAMES = ['messages', 'campaigns', 'sessions', 'message-logs', 'campaign-logs', 'subscriptions', 'emails'];

// Force light mode theme
const lightTheme = createTheme({
    palette: {
        mode: 'light'
    }
});

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function UserOverview() {
    // Support-only view: always render in English regardless of the agent's
    // i18n language. Do NOT use t() here — labels must not be translated.
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const supportedUserId = searchParams.get('supportedUserId');
    
    // Get initial tab from URL or default to messages (0)
    const initialTab = (() => {
        const tabName = searchParams.get('tab');
        const tabIndex = TAB_NAMES.indexOf(tabName || '');
        return tabIndex >= 0 ? tabIndex : 0;
    })();
    const [tabValue, setTabValue] = useState(initialTab);
    const [isLoading, setIsLoading] = useState(false);

    const totalMessages = useAppSelector((state) => state.DBUserMessageReducer.total || 0);
    const totalCampaigns = useAppSelector((state) => state.DBCampaignReducer.total || 0);

    const user = useAppSelector((state) => supportedUserId ? state.DBUserReducer.entities[supportedUserId] : undefined);
    const latestSession = useAppSelector((state) => {
        const userSessions = Object.values(state.DBSessionReducer.entities)
            .filter((session): session is NonNullable<typeof session> => !!session && session.owner === supportedUserId)
            .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime());
        return userSessions[0];
    });

    const allSubscriptions = useAppSelector(state => subscriptionSelectByQuery(state.DBSubscriptionReducer, {})) || [];
    const currentWorkspace = useAppSelector(selectCurrentWorkspace) as Workspace;
    const latestSubscription = useMemo(() => {
        console.log('Current Workspace:', currentWorkspace);
        console.log('All Subscriptions:', allSubscriptions);
        
        const workspaceSubscriptions = allSubscriptions.filter(sub => 
            sub.owner === supportedUserId && 
            sub.workspaceId === currentWorkspace?._id
        );
        console.log('Workspace Subscriptions:', workspaceSubscriptions);
        
        const activeSubscriptions = workspaceSubscriptions.filter(sub => 
            sub.status.toLowerCase() === 'active'
        );
        console.log('Active Workspace Subscriptions:', activeSubscriptions);

        const sortedSubscriptions = activeSubscriptions
            .map(sub => new SubscriptionObject(sub))
            .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));
        
        return sortedSubscriptions[0];
    }, [allSubscriptions, supportedUserId, currentWorkspace]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        // Update URL with new tab
        const params = new URLSearchParams(location.search);
        params.set('tab', TAB_NAMES[newValue]);
        navigate({ search: params.toString() }, { replace: true });
    };

    // Update tab value if URL changes
    useEffect(() => {
        const tabFromUrl = TAB_NAMES.indexOf(searchParams.get('tab') || '');
        if (tabFromUrl >= 0 && tabFromUrl !== tabValue) {
            setTabValue(tabFromUrl);
        } else if (tabFromUrl === -1 && tabValue !== 0) {
            // If invalid tab in URL, default to messages
            setTabValue(0);
            const params = new URLSearchParams(location.search);
            params.set('tab', TAB_NAMES[0]);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [location.search, navigate, searchParams, tabValue]);

    // Load all data when component mounts
    useEffect(() => {
        if (supportedUserId) {
            setIsLoading(true);
            Promise.all([
                dispatch(DBUserMessageThunks.find({
                    owner: supportedUserId,
                    $sort: { createdAt: -1 }
                })),
                dispatch(DBMessageLogThunks.find({
                    userId: supportedUserId,
                    $sort: { _id: -1 },
                    $skip: 0,
                    $limit: 500,
                    cleanStateBeforeFetch: true
                })),
                dispatch(DBSessionThunks.find({
                    owner: supportedUserId,
                    $sort: { updatedAt: -1 }
                })),
                dispatch(DBCampaignThunks.find({
                    owner: supportedUserId
                })),
                dispatch(DBCampaignLogThunks.find({
                    userId: supportedUserId,
                    $sort: { createdAt: -1 },
                    $limit: -1
                })),
                dispatch(DBSubscriptionThunks.find({})),
                dispatch(DBEmailSendThunks.find({
                    userId: supportedUserId,
                    $sort: { sentAt: -1 }
                })),
                dispatch(connectNotifierAsSupportedUser({ supportedUserId }))
            ]).finally(() => {
                setIsLoading(false);
            });
        }
    }, [supportedUserId, dispatch]);

    if (!supportedUserId) {
        return (
            <ThemeProvider theme={lightTheme}>
                <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        No user ID provided
                    </Typography>
                </Container>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={lightTheme}>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <UserOverviewHeader
                    supportedUserId={supportedUserId}
                    user={user}
                    currentWorkspace={currentWorkspace}
                    latestSession={latestSession}
                    latestSubscription={latestSubscription}
                    totalMessages={totalMessages}
                    totalCampaigns={totalCampaigns}
                />

                {/* Tabs Section */}
                <Paper elevation={3}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '1rem'
                            }
                        }}
                    >
                        <Tab label="Messages" icon={<MessageIcon />} iconPosition="start" />
                        <Tab label="Campaigns" icon={<CampaignIcon />} iconPosition="start" />
                        <Tab label="Sessions" icon={<ForumIcon />} iconPosition="start" />
                        <Tab label="Message Logs" icon={<HistoryIcon />} iconPosition="start" />
                        <Tab label="Campaign Logs" icon={<HistoryIcon />} iconPosition="start" />
                        <Tab label="Subscriptions" icon={<SubscriptionsIcon />} iconPosition="start" />
                        <Tab label="Emails" icon={<EmailIcon />} iconPosition="start" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <MessagesTab supportedUserId={supportedUserId} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <CampaignsTab supportedUserId={supportedUserId} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <SessionsTab supportedUserId={supportedUserId} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <MessageLogsTab supportedUserId={supportedUserId} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={4}>
                        <CampaignLogsTab supportedUserId={supportedUserId} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={5}>
                        <SubscriptionsTab supportedUserId={supportedUserId} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={6}>
                        <EmailsTab supportedUserId={supportedUserId} />
                    </TabPanel>
                </Paper>
            </Container>
        </ThemeProvider>
    );
} 